import { ArrowRight, Phone, ShieldCheck, HeartHandshake, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND } from "../lib/constants";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d4897236-428b-4abf-b3c0-e7679c5a4973/images/17dacbd358c08b08a71c9b212586df1c0a023f3cb13620d360997562ecb866ab.png";

const TRUST_BADGES = [
  { icon: HeartHandshake, label: "Personalized Care" },
  { icon: ShieldCheck, label: "Dependable Caregivers" },
  { icon: HeartHandshake, label: "Family Peace of Mind" },
  { icon: MapPin, label: "Locally Focused" },
  { icon: ShieldCheck, label: "Compassionate Support" },
];

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end pb-16 md:pb-24 pt-32 md:pt-40 overflow-hidden"
    >
      {/* Background image with ken-burns */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div
          className="absolute inset-0 ken-burns bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#005165]/95 via-[#005165]/80 to-[#005165]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#005165]/85 via-[#005165]/30 to-[#005165]/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <div className="max-w-3xl">
          <div className="gold-divider mb-6 text-[#F8F9FA]" data-testid="hero-eyebrow">
            <span style={{ color: "#C4B088" }}>
              In-Home Senior Care · Madison, WI
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[#FFFFFF] text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight"
          >
            Compassionate <em className="not-italic text-gradient-gold">in-home care</em> that helps your loved ones stay safe, independent, and comfortable at home.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-lg md:text-xl text-[#F8F9FA]/90 font-light max-w-2xl leading-relaxed"
          >
            Madison Peaceful Path Home Care provides personalized, relationship-based support for
            seniors and families throughout Madison, Wisconsin. We help families create peace of
            mind through dependable care, meaningful connections, and compassionate support.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid="hero-schedule-consultation"
              onClick={() => scrollTo("contact")}
              className="bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-14 px-8 text-base btn-shimmer"
            >
              Schedule A Free Care Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href={`tel:${BRAND.phoneRaw}`} data-testid="hero-call-now">
              <Button className="bg-[#FFFFFF] hover:bg-white text-[#005165] rounded-full h-14 px-8 text-base btn-shimmer">
                <Phone className="w-5 h-5 mr-2" /> Call Madison Peaceful Path
              </Button>
            </a>
            <Button
              data-testid="hero-apply-caregiver"
              onClick={() => (window.location.href = "/careers")}
              variant="outline"
              className="border-[#FFFFFF]/40 bg-transparent text-[#FFFFFF] hover:bg-[#FFFFFF] hover:text-[#005165] rounded-full h-14 px-8 text-base"
            >
              Apply As A Caregiver
            </Button>
          </div>

          {/* Trust badges */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl">
            {TRUST_BADGES.map((b) => (
              <div
                key={b.label}
                data-testid={`trust-badge-${b.label.toLowerCase().replace(/ /g, "-")}`}
                className="flex items-center gap-2 px-4 py-3 rounded-full bg-[#FFFFFF]/10 backdrop-blur-md border border-[#FFFFFF]/15 text-[#FFFFFF] text-xs sm:text-[13px]"
              >
                <b.icon className="w-4 h-4 text-[#C4B088] shrink-0" />
                <span className="font-medium">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll cue */}
        <div className="hidden md:flex absolute right-10 bottom-10 flex-col items-center gap-2 text-[#FFFFFF]/70">
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <span className="w-px h-12 bg-gradient-to-b from-[#FFFFFF]/70 to-transparent" />
        </div>
      </div>
    </section>
  );
}
